import React, { useState, useEffect } from 'react';
import { EULAModal } from './modals/EULAModal';
import { Button } from './';
import { Form, Input, Checkbox, Alert } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLazyGetActuatorInfoQuery, useRegisterUserMutation } from '../api';
import { getUrlParams, validatePassword } from '../lib/helpers';
import { ValidationErrors } from '../constants';
import { useNotificationContext } from '../providers';
import '../styles/form.scss';

export const RegisterForm = () => {
    const [form] = Form.useForm();
    const [ldapEnabled, setLdapEnabled] = useState(false);
    const [registerMethod, setRegisterMethod] = useState('');
    const [registerUser, { isLoading }] = useRegisterUserMutation();
    const [getVersionInfo] = useLazyGetActuatorInfoQuery();

    const [isEulaModalVisible, setIsEulaModalVisible] = useState(false);
    const [eulaOpened, setEulaOpened] = useState(false);

    const { notifyError, notifySuccess } = useNotificationContext();

    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [token, setToken] = useState('');

    form.setFieldsValue({ email });

    useEffect(() => {
        const urlParams = getUrlParams();
        const emailParam = decodeURIComponent(urlParams['email'] || '') || null;
        const tokenParam = decodeURIComponent(urlParams['token'] || '') || null;

        setEmail(emailParam);
        setToken(tokenParam);

        const fetchVersionInfo = async () => {
            try {
                const versionInfo = await getVersionInfo();
                if (versionInfo && versionInfo.ldap_enabled) {
                    setLdapEnabled(versionInfo.ldap_enabled);
                }
            } catch (error) {
                console.error('Error fetching version info:', error);
            }
        };
        fetchVersionInfo();
    }, [location.search]);

    const onFinish = async (values) => {
        try {
            const payload = {
                email,
                token,
                eula_accepted: values.eulaAcceptance,
                credentials: {
                    type: registerMethod === 'ldap' ? 'ldap' : 'password',
                    username: registerMethod === 'ldap' ? values.ldapUser : email,
                    password: values.password,
                },
            };

            await registerUser(payload).unwrap();
            notifySuccess({
                message: 'Registration successful',
                description: 'You can now log in with your new account.',
            });
            navigate('/?mode=welcome');
        } catch (error) {
            notifyError({ message: 'Error', description: 'An error occurred during registration.' });
        }
    };

    return (
        <div className="register-panel">
            <div className="panel panel-default padding-bottom-20">
                <h3>Register</h3>
                <Form
                    form={form}
                    name="register"
                    style={{ maxWidth: 500 }}
                    layout="vertical"
                    onFinish={onFinish}
                    className="register-form"
                >
                    {ldapEnabled && (
                        <Form.Item
                            name="registrationMethod"
                            label="Register with"
                            rules={[{ required: true, message: 'Please select a registration method!' }]}
                        >
                            <Input.Select onChange={(value) => setRegisterMethod(value)} defaultValue="ldap">
                                <Input.Option value="ldap">LDAP Credentials</Input.Option>
                                <Input.Option value="rm">New RiverMeadow Account</Input.Option>
                            </Input.Select>
                        </Form.Item>
                    )}

                    {email && (
                        <Form.Item name="email" label="Email">
                            <Input disabled />
                        </Form.Item>
                    )}

                    {ldapEnabled && registerMethod === 'ldap' && (
                        <Form.Item
                            name="ldapUser"
                            label="Username"
                            rules={[{ required: true, message: ValidationErrors.LDAP }]}
                        >
                            <Input />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[
                            {
                                required: true,
                                message: ValidationErrors.password,
                            },
                            {
                                validator: (_, value) => {
                                    const errors = [];
                                    validatePassword(value, errors);
                                    return errors.length
                                        ? Promise.reject(
                                              <>
                                                  {errors.map((err, idx) => (
                                                      <span key={idx}>{err}</span>
                                                  ))}
                                              </>,
                                          )
                                        : Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        label="Confirm Password"
                        dependencies={['password']}
                        rules={[
                            { required: true, message: ValidationErrors.confirmPassword },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(ValidationErrors.matchPassword));
                                },
                            }),
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <div style={{ textAlign: 'right' }}>
                        <Form.Item
                            name="eulaAcceptance"
                            valuePropName="checked"
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(ValidationErrors.termsAndConditions));
                                    },
                                },
                            ]}
                        >
                            <Checkbox className={'eula-checkbox'} disabled={!eulaOpened}>
                                I have read and agree to the{' '}
                                <a
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsEulaModalVisible(true);
                                        setEulaOpened(true);
                                    }}
                                >
                                    Terms & Conditions
                                </a>
                                .
                            </Checkbox>
                        </Form.Item>
                        {!eulaOpened && (
                            <Alert
                                className={'alert-message'}
                                title="Please open the Terms & Conditions and review them before proceeding"
                                type="warning"
                            />
                        )}
                    </div>

                    <Form.Item style={{ textAlign: 'right' }}>
                        <Button type="primary" htmlType="submit" className="btn-register" loading={isLoading}>
                            Register
                        </Button>
                    </Form.Item>
                </Form>
            </div>
            <EULAModal open={isEulaModalVisible} onCancel={() => setIsEulaModalVisible(false)} />
        </div>
    );
};
